import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from 'react';

/**
 * Alternative Runtime Provider
 *
 * Provides preview capabilities for non-Node.js frameworks:
 * - Python (Flask, Django, FastAPI) via Pyodide
 * - Flutter via DartPad embed or compiled WASM
 * - Rust via Rust Playground embed or WASM
 * - Go via Go Playground embed or TinyGo WASM
 *
 * All solutions support BOTH iframe preview AND preview URL
 */

export type RuntimeType =
  | 'pyodide' // Python in browser via Pyodide/PyScript
  | 'dartpad' // Flutter via DartPad embed
  | 'rust-playground' // Rust via Rust Playground
  | 'go-playground' // Go via Go Playground
  | 'php-playground' // PHP via 3v4l.org or PHP sandbox
  | 'godot-editor' // Godot via Web Editor (opens in new tab)
  | 'godot-web' // Godot HTML5 export (WASM) running in iframe
  | 'unity-web' // Unity WebGL export running in iframe
  | 'express-playground' // Express via mock server simulation
  | 'wasm' // Generic WASM runtime
  | null;

export type PreviewMode = 'iframe' | 'url' | 'both';

export interface AlternativeRuntimeContextType {
  // Current runtime
  runtime: RuntimeType;
  setRuntime: (runtime: RuntimeType) => void;

  // Preview URL that can be opened in new tab
  previewUrl: string | null;

  // HTML content for iframe src or srcDoc
  previewHtml: string | null;

  // Status
  isLoading: boolean;
  error: string | null;

  // Actions
  generatePythonPreview: (files: Record<string, string>) => Promise<void>;
  generateFlutterPreview: (files: Record<string, string>) => Promise<void>;
  generateRustPreview: (files: Record<string, string>) => Promise<void>;
  generateGoPreview: (files: Record<string, string>) => Promise<void>;
  generatePhpPreview: (files: Record<string, string>) => Promise<void>;
  generateGodotPreview: (files: Record<string, string>) => Promise<void>;
  generateExpressPreview: (files: Record<string, string>) => Promise<void>;

  // Detect framework from files
  detectFramework: (files: Record<string, string>) => RuntimeType;

  // Clear preview
  clearPreview: () => void;
}

const AlternativeRuntimeContext = createContext<AlternativeRuntimeContextType | undefined>(
  undefined,
);

/**
 * Generate PyScript HTML that runs Python code in browser
 * This creates a self-contained HTML page that can be served at a URL or embedded
 */
function generatePyScriptHtml(pythonCode: string, title: string = 'Python App'): string {
  // Escape code for embedding in HTML
  const escapedCode = pythonCode
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <link rel="stylesheet" href="https://pyscript.net/releases/2024.1.1/core.css">
  <script type="module" src="https://pyscript.net/releases/2024.1.1/core.js"></script>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      min-height: 100vh;
      padding: 20px;
    }
    #app-container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      padding: 24px;
      min-height: calc(100vh - 40px);
    }
    h1 {
      color: #1a1a2e;
      margin-bottom: 16px;
      font-size: 24px;
    }
    .output {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 16px;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 14px;
      white-space: pre-wrap;
      margin-top: 16px;
    }
    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      color: #666;
    }
    .loading::after {
      content: '';
      width: 24px;
      height: 24px;
      border: 3px solid #ddd;
      border-top-color: #667eea;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-left: 12px;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    /* PyScript specific styles */
    py-terminal {
      background: #1a1a2e !important;
      border-radius: 8px;
      margin-top: 16px;
    }
  </style>
</head>
<body>
  <div id="app-container">
    <h1>${title}</h1>
    <div id="loading" class="loading">Initializing Python runtime...</div>
    <div id="output" class="output" style="display: none;"></div>
    <py-terminal></py-terminal>
  </div>

  <script type="py">
from pyscript import display, document

# Hide loading indicator
document.getElementById('loading').style.display = 'none'
document.getElementById('output').style.display = 'block'

# Run the user's Python code
${pythonCode}
  </script>
</body>
</html>`;
}

/**
 * Generate Flask-like API simulation using PyScript
 * Creates a mock server that handles routes in-browser
 */
function generateFlaskSimulationHtml(files: Record<string, string>): string {
  // Find main.py or app.py
  const mainFile = files['main.py'] || files['app.py'] || files['server.py'] || '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Python API Preview</title>
  <link rel="stylesheet" href="https://pyscript.net/releases/2024.1.1/core.css">
  <script type="module" src="https://pyscript.net/releases/2024.1.1/core.js"></script>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      min-height: 100vh;
      padding: 20px;
    }
    .container {
      max-width: 900px;
      margin: 0 auto;
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      overflow: hidden;
    }
    .header {
      background: #1a1a2e;
      color: white;
      padding: 16px 24px;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .header-icon {
      width: 32px;
      height: 32px;
      background: #38ef7d;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .content { padding: 24px; }
    .endpoint-tester {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;
    }
    .endpoint-row {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;
    }
    select, input, button {
      padding: 10px 14px;
      border: 1px solid #ddd;
      border-radius: 6px;
      font-size: 14px;
    }
    select { width: 100px; background: white; }
    input { flex: 1; }
    button {
      background: #667eea;
      color: white;
      border: none;
      cursor: pointer;
      font-weight: 500;
    }
    button:hover { background: #5a6fd6; }
    .response {
      background: #1a1a2e;
      color: #38ef7d;
      padding: 16px;
      border-radius: 8px;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 13px;
      white-space: pre-wrap;
      max-height: 300px;
      overflow-y: auto;
    }
    .routes-list {
      margin-top: 16px;
      padding: 16px;
      background: #f0f4f8;
      border-radius: 8px;
    }
    .route-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid #e2e8f0;
    }
    .route-item:last-child { border-bottom: none; }
    .method-badge {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }
    .method-get { background: #d4edda; color: #155724; }
    .method-post { background: #cce5ff; color: #004085; }
    .method-put { background: #fff3cd; color: #856404; }
    .method-delete { background: #f8d7da; color: #721c24; }
    py-terminal { display: none; }
    .loading {
      text-align: center;
      padding: 40px;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="header-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
      </div>
      <div>
        <strong>Python API Preview</strong>
        <div style="font-size: 12px; opacity: 0.7;">Powered by PyScript</div>
      </div>
    </div>
    <div class="content">
      <div id="loading" class="loading">
        <p>Starting Python runtime...</p>
      </div>
      <div id="app" style="display: none;">
        <div class="endpoint-tester">
          <h3 style="margin-bottom: 12px;">Test API Endpoints</h3>
          <div class="endpoint-row">
            <select id="method">
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
            <input type="text" id="endpoint" placeholder="/api/endpoint" value="/">
            <button onclick="testEndpoint()">Send</button>
          </div>
          <div class="response" id="response">// Response will appear here</div>
        </div>
        <div class="routes-list" id="routes-list">
          <h4 style="margin-bottom: 12px;">Available Routes</h4>
          <div id="routes-container"></div>
        </div>
      </div>
    </div>
  </div>

  <py-terminal></py-terminal>

  <script type="py">
from pyscript import display, document
import json

# Simple Flask-like router simulation
class MockFlask:
    def __init__(self, name):
        self.name = name
        self.routes = {}

    def route(self, path, methods=None):
        if methods is None:
            methods = ['GET']
        def decorator(func):
            for method in methods:
                key = f"{method}:{path}"
                self.routes[key] = func
            return func
        return decorator

    def get(self, path):
        return self.route(path, methods=['GET'])

    def post(self, path):
        return self.route(path, methods=['POST'])

    def handle_request(self, method, path):
        key = f"{method}:{path}"
        if key in self.routes:
            try:
                result = self.routes[key]()
                if isinstance(result, dict):
                    return json.dumps(result, indent=2)
                return str(result)
            except Exception as e:
                return json.dumps({"error": str(e)}, indent=2)
        return json.dumps({"error": f"Route {method} {path} not found"}, indent=2)

    def get_routes(self):
        routes = []
        for key in self.routes:
            method, path = key.split(':', 1)
            routes.append({"method": method, "path": path})
        return routes

# Create mock Flask app
app = MockFlask(__name__)

# Alias for FastAPI style
class MockFastAPI(MockFlask):
    pass

FastAPI = MockFastAPI

# Define some default routes
@app.route('/')
def index():
    return {"message": "Hello from Python!", "status": "running"}

@app.route('/api/health')
def health():
    return {"status": "healthy", "runtime": "PyScript"}

# Try to load user code
try:
    exec("""${mainFile.replace(/`/g, '\\`').replace(/\$/g, '\\$')}""")
except Exception as e:
    print(f"Error loading user code: {e}")

# Update UI
document.getElementById('loading').style.display = 'none'
document.getElementById('app').style.display = 'block'

# Populate routes list
routes_html = ""
for route in app.get_routes():
    method_class = f"method-{route['method'].lower()}"
    routes_html += f'''
        <div class="route-item">
            <span class="method-badge {method_class}">{route['method']}</span>
            <code>{route['path']}</code>
        </div>
    '''
document.getElementById('routes-container').innerHTML = routes_html

# Make test function available to JavaScript
def test_endpoint():
    method = document.getElementById('method').value
    endpoint = document.getElementById('endpoint').value
    result = app.handle_request(method, endpoint)
    document.getElementById('response').textContent = result

# Expose to global scope
import js
js.window.pyTestEndpoint = test_endpoint
  </script>

  <script>
    function testEndpoint() {
      if (window.pyTestEndpoint) {
        window.pyTestEndpoint();
      }
    }
  </script>
</body>
</html>`;
}

/**
 * Generate DartPad embed HTML for Flutter
 */
function generateDartPadHtml(dartCode: string, title: string = 'Flutter App'): string {
  // For DartPad, we need to create a gist or use inline code
  // DartPad supports embedding with a gist ID or inline code via postMessage

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #1a1a2e;
      min-height: 100vh;
    }
    .header {
      background: #0175c2;
      color: white;
      padding: 12px 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .header svg { width: 24px; height: 24px; }
    .dartpad-container {
      width: 100%;
      height: calc(100vh - 48px);
    }
    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
    .fallback {
      padding: 40px;
      text-align: center;
      color: #ccc;
    }
    .fallback code {
      display: block;
      background: #2d2d2d;
      padding: 20px;
      border-radius: 8px;
      margin-top: 20px;
      text-align: left;
      white-space: pre-wrap;
      font-size: 13px;
      max-height: 400px;
      overflow-y: auto;
    }
  </style>
</head>
<body>
  <div class="header">
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.105 4.105S9.158 1.58 11.684.316a3.079 3.079 0 0 1 1.481-.315c.766.047 1.677.788 1.677.788L24 9.948v9.789h-4.263V24H9.789l-9-9c-1.188-1.188-1.188-3.161 0-4.395l3.316-3.316v-3.184z"/>
    </svg>
    <span><strong>Flutter Preview</strong> - Powered by DartPad</span>
  </div>
  <div class="dartpad-container">
    <iframe
      src="https://dartpad.dev/embed-flutter.html?theme=dark&split=60&run=true"
      id="dartpad-frame"
      allow="clipboard-read; clipboard-write"
    ></iframe>
  </div>
  <script>
    // Send code to DartPad via postMessage
    const dartCode = ${JSON.stringify(dartCode)};
    const iframe = document.getElementById('dartpad-frame');

    iframe.onload = function() {
      // Wait a bit for DartPad to initialize
      setTimeout(() => {
        iframe.contentWindow.postMessage({
          type: 'sourceCode',
          sourceCode: dartCode
        }, '*');
      }, 2000);
    };
  </script>
</body>
</html>`;
}

/**
 * Generate Rust Playground embed HTML
 * Uses the Rust Playground's code parameter to pre-fill the editor with user's code
 */
function generateRustPlaygroundHtml(rustCode: string, title: string = 'Rust App'): string {
  // Encode the code for URL - Rust Playground accepts 'code' parameter
  const encodedCode = encodeURIComponent(rustCode);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #1a1a2e;
      min-height: 100vh;
    }
    .header {
      background: #f74c00;
      color: white;
      padding: 12px 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .header-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;
    }
    .header-actions button {
      background: rgba(255,255,255,0.2);
      border: 1px solid rgba(255,255,255,0.3);
      color: white;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .header-actions button:hover {
      background: rgba(255,255,255,0.3);
    }
    .playground-container {
      width: 100%;
      height: calc(100vh - 48px);
    }
    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  </style>
</head>
<body>
  <div class="header">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.834 8.101a13.912 13.912 0 0 1-13.643 11.72 10.105 10.105 0 0 1-1.994-.12 6.111 6.111 0 0 1-5.082-5.761 5.934 5.934 0 0 1 11.867-.084c.025.983-.401 1.846-1.277 1.871-.936 0-1.374-.668-1.374-1.567v-2.5a1.531 1.531 0 0 0-1.52-1.533H8.715a3.648 3.648 0 1 0 2.695 6.08l.073-.11.074.121a2.58 2.58 0 0 0 2.2 1.048 2.909 2.909 0 0 0 2.695-3.04 7.912 7.912 0 0 0-.217-1.933 7.404 7.404 0 0 0-14.64 1.603 7.497 7.497 0 0 0 7.308 7.405s.549.05 1.167.035a15.803 15.803 0 0 0 8.475-2.528c.036-.025.072.025.048.061a12.44 12.44 0 0 1-9.69 3.963 8.744 8.744 0 0 1-8.9-8.972 9.049 9.049 0 0 1 3.635-7.247 8.863 8.863 0 0 1 5.229-1.726h2.813a7.915 7.915 0 0 0 5.839-2.578.11.11 0 0 1 .059-.034.112.112 0 0 1 .12.053.113.113 0 0 1 .015.067 7.934 7.934 0 0 1-1.227 3.549.107.107 0 0 0-.014.06.11.11 0 0 0 .073.095.109.109 0 0 0 .062.004 8.505 8.505 0 0 0 5.913-4.876.155.155 0 0 1 .055-.053.15.15 0 0 1 .147 0 .153.153 0 0 1 .054.053A10.779 10.779 0 0 1 23.834 8.1zM8.895 11.628a2.188 2.188 0 1 0 2.188 2.188 2.188 2.188 0 0 0-2.188-2.188z"/>
    </svg>
    <span><strong>Rust Preview</strong> - Your code in Rust Playground</span>
    <div class="header-actions">
      <button onclick="window.open('https://play.rust-lang.org/?version=stable&mode=debug&edition=2021&code=${encodedCode}', '_blank')">
        Open in New Tab
      </button>
    </div>
  </div>
  <div class="playground-container">
    <iframe src="https://play.rust-lang.org/?version=stable&mode=debug&edition=2021&code=${encodedCode}"></iframe>
  </div>
</body>
</html>`;
}

/**
 * Generate Go Playground embed HTML
 * Go Playground doesn't support URL parameters for code, so we use a custom UI
 * that allows running Go code via the Go Playground API
 */
function generateGoPlaygroundHtml(goCode: string, title: string = 'Go App'): string {
  // Escape the code for embedding in JavaScript
  const escapedCode = JSON.stringify(goCode);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #1a1a2e;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .header {
      background: #00add8;
      color: white;
      padding: 12px 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .header-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;
    }
    .header-actions button {
      background: rgba(255,255,255,0.2);
      border: 1px solid rgba(255,255,255,0.3);
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: background 0.2s;
    }
    .header-actions button:hover {
      background: rgba(255,255,255,0.3);
    }
    .header-actions button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .header-actions button.run {
      background: #fff;
      color: #00add8;
      border-color: #fff;
    }
    .header-actions button.run:hover {
      background: #e0f7fa;
    }
    .container {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .editor-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .code-editor {
      flex: 1;
      width: 100%;
      background: #282c34;
      color: #abb2bf;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      line-height: 1.5;
      padding: 16px;
      border: none;
      resize: none;
      outline: none;
      tab-size: 4;
    }
    .output-container {
      background: #1e1e1e;
      border-top: 2px solid #00add8;
      max-height: 40%;
      overflow-y: auto;
    }
    .output-header {
      background: #252526;
      padding: 8px 16px;
      font-size: 12px;
      color: #888;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .output-content {
      padding: 16px;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 13px;
      white-space: pre-wrap;
      color: #d4d4d4;
      min-height: 60px;
    }
    .output-content.error {
      color: #f44747;
    }
    .output-content.success {
      color: #4ec9b0;
    }
    .spinner {
      display: inline-block;
      width: 14px;
      height: 14px;
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 50%;
      border-top-color: #fff;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  </style>
</head>
<body>
  <div class="header">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M1.811 10.231c-.047 0-.058-.023-.035-.059l.246-.315c.023-.035.081-.058.128-.058h4.172c.046 0 .058.035.035.07l-.199.303c-.023.036-.082.07-.117.07zM.047 11.306c-.047 0-.059-.023-.035-.058l.245-.316c.023-.035.082-.058.129-.058h5.328c.047 0 .07.035.058.07l-.093.28c-.012.047-.058.07-.105.07zm2.828 1.075c-.047 0-.059-.035-.035-.07l.163-.292c.023-.035.07-.07.117-.07h2.337c.047 0 .07.035.07.082l-.023.28c0 .047-.047.082-.082.082zm12.129-2.36c-.736.187-1.239.327-1.963.514-.176.046-.187.058-.34-.117-.174-.199-.303-.327-.548-.444-.737-.362-1.45-.257-2.115.175-.795.514-1.204 1.274-1.192 2.22.011.935.654 1.706 1.577 1.835.795.105 1.46-.175 1.987-.77.105-.13.198-.27.315-.434H10.47c-.245 0-.304-.152-.222-.35.152-.362.432-.97.596-1.274a.315.315 0 0 1 .292-.187h4.253c-.023.316-.023.631-.07.947a4.983 4.983 0 0 1-.958 2.29c-.841 1.11-1.94 1.8-3.33 1.986-1.145.152-2.209-.07-3.143-.77-.865-.655-1.356-1.52-1.484-2.595-.152-1.274.222-2.419.993-3.424.83-1.086 1.928-1.776 3.272-2.02 1.098-.2 2.15-.07 3.096.571.62.41 1.063.97 1.356 1.648.07.105.023.164-.117.2m3.868 6.461c-1.064-.024-2.034-.328-2.852-1.029a3.665 3.665 0 0 1-1.262-2.255c-.21-1.32.152-2.489.947-3.529.853-1.122 1.881-1.706 3.272-1.95 1.192-.21 2.314-.095 3.33.595.923.63 1.496 1.484 1.648 2.605.198 1.578-.257 2.863-1.344 3.962-.771.783-1.718 1.273-2.805 1.495-.315.06-.63.07-.934.106zm2.78-4.72c-.011-.153-.011-.27-.034-.387-.21-1.157-1.274-1.81-2.384-1.554-1.087.245-1.788.935-2.045 2.033-.21.912.234 1.835 1.075 2.21.643.28 1.285.244 1.905-.07.923-.48 1.425-1.228 1.484-2.233z"/>
    </svg>
    <span><strong>Go Preview</strong> - Run your Go code</span>
    <div class="header-actions">
      <button class="run" onclick="runCode()" id="runBtn">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        Run
      </button>
      <button onclick="formatCode()" id="fmtBtn">Format</button>
      <button onclick="window.open('https://go.dev/play/', '_blank')">
        Open Go Playground
      </button>
    </div>
  </div>
  <div class="container">
    <div class="editor-container">
      <textarea class="code-editor" id="code" spellcheck="false">${goCode.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
    </div>
    <div class="output-container">
      <div class="output-header">
        <span>Output</span>
        <span id="status"></span>
      </div>
      <div class="output-content" id="output">Click "Run" to execute your Go code</div>
    </div>
  </div>

  <script>
    const GO_PLAYGROUND_API = 'https://go.dev/_/compile';
    const GO_PLAYGROUND_FMT = 'https://go.dev/_/fmt';

    async function runCode() {
      const code = document.getElementById('code').value;
      const output = document.getElementById('output');
      const status = document.getElementById('status');
      const runBtn = document.getElementById('runBtn');

      runBtn.disabled = true;
      runBtn.innerHTML = '<span class="spinner"></span> Running...';
      output.className = 'output-content';
      output.textContent = 'Compiling and running...';
      status.textContent = '';

      try {
        const response = await fetch(GO_PLAYGROUND_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'version=2&body=' + encodeURIComponent(code) + '&withVet=true'
        });

        const result = await response.json();

        if (result.Errors) {
          output.className = 'output-content error';
          output.textContent = result.Errors;
          status.textContent = 'Compilation failed';
        } else if (result.Events && result.Events.length > 0) {
          output.className = 'output-content success';
          output.textContent = result.Events.map(e => e.Message).join('');
          status.textContent = 'Completed successfully';
        } else {
          output.className = 'output-content';
          output.textContent = '(no output)';
          status.textContent = 'Completed';
        }
      } catch (err) {
        output.className = 'output-content error';
        output.textContent = 'Error: ' + err.message + '\\n\\nNote: This may be due to CORS restrictions. Try opening in Go Playground directly.';
        status.textContent = 'Failed';
      } finally {
        runBtn.disabled = false;
        runBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg> Run';
      }
    }

    async function formatCode() {
      const code = document.getElementById('code');
      const fmtBtn = document.getElementById('fmtBtn');
      const status = document.getElementById('status');

      fmtBtn.disabled = true;
      fmtBtn.textContent = 'Formatting...';

      try {
        const response = await fetch(GO_PLAYGROUND_FMT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'body=' + encodeURIComponent(code.value) + '&imports=true'
        });

        const result = await response.json();

        if (result.Error) {
          status.textContent = 'Format error: ' + result.Error;
        } else {
          code.value = result.Body;
          status.textContent = 'Formatted';
        }
      } catch (err) {
        status.textContent = 'Format failed: ' + err.message;
      } finally {
        fmtBtn.disabled = false;
        fmtBtn.textContent = 'Format';
      }
    }

    // Support Ctrl+Enter to run
    document.getElementById('code').addEventListener('keydown', function(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        runCode();
      }
      // Tab support
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = this.selectionStart;
        const end = this.selectionEnd;
        this.value = this.value.substring(0, start) + '\\t' + this.value.substring(end);
        this.selectionStart = this.selectionEnd = start + 1;
      }
    });
  </script>
</body>
</html>`;
}

/**
 * Generate PHP Playground HTML
 * Uses a custom UI with the ability to run PHP code via external services
 * Since PHP can't run in browser natively, we provide code editing and external execution options
 */
function generatePhpPlaygroundHtml(phpCode: string, title: string = 'PHP App'): string {
  // Encode the code for URL
  const encodedCode = encodeURIComponent(phpCode);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #1a1a2e;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .header {
      background: #777bb4;
      color: white;
      padding: 12px 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .header-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;
    }
    .header-actions button {
      background: rgba(255,255,255,0.2);
      border: 1px solid rgba(255,255,255,0.3);
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: background 0.2s;
    }
    .header-actions button:hover {
      background: rgba(255,255,255,0.3);
    }
    .header-actions button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .header-actions button.run {
      background: #fff;
      color: #777bb4;
      border-color: #fff;
    }
    .header-actions button.run:hover {
      background: #f0f0f5;
    }
    .container {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .editor-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .code-editor {
      flex: 1;
      width: 100%;
      background: #282c34;
      color: #abb2bf;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      line-height: 1.5;
      padding: 16px;
      border: none;
      resize: none;
      outline: none;
      tab-size: 4;
    }
    .output-container {
      background: #1e1e1e;
      border-top: 2px solid #777bb4;
      max-height: 40%;
      overflow-y: auto;
    }
    .output-header {
      background: #252526;
      padding: 8px 16px;
      font-size: 12px;
      color: #888;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .output-content {
      padding: 16px;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 13px;
      white-space: pre-wrap;
      color: #d4d4d4;
      min-height: 60px;
    }
    .output-content.error {
      color: #f44747;
    }
    .output-content.success {
      color: #4ec9b0;
    }
    .output-content.html-output {
      background: white;
      color: #333;
      border-radius: 4px;
    }
    .spinner {
      display: inline-block;
      width: 14px;
      height: 14px;
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 50%;
      border-top-color: #fff;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    .info-banner {
      background: rgba(119, 123, 180, 0.2);
      padding: 12px 16px;
      font-size: 13px;
      color: #ccc;
      border-bottom: 1px solid rgba(119, 123, 180, 0.3);
    }
    .info-banner a {
      color: #a5a8e0;
    }
  </style>
</head>
<body>
  <div class="header">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-.5 4.5h3c.275 0 .5.225.5.5v1c0 .275-.225.5-.5.5H12v4.5c0 .275-.225.5-.5.5h-1c-.275 0-.5-.225-.5-.5V8.5h-.5c-.275 0-.5-.225-.5-.5v-1c0-.275.225-.5.5-.5h1c.275 0 .5.225.5.5v.5zm5.5 7.5v1c0 .275-.225.5-.5.5h-3c-.275 0-.5-.225-.5-.5v-4.5h-.5c-.275 0-.5-.225-.5-.5v-1c0-.275.225-.5.5-.5h1c.275 0 .5.225.5.5v4.5h2.5c.275 0 .5.225.5.5z"/>
    </svg>
    <span><strong>PHP Preview</strong> - Laravel/PHP Code</span>
    <div class="header-actions">
      <button class="run" onclick="runOnline()" id="runBtn">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        Run Online
      </button>
      <button onclick="openIn3v4l()">Open in 3v4l.org</button>
      <button onclick="openInPhpFiddle()">PHP Fiddle</button>
    </div>
  </div>
  <div class="info-banner">
    PHP code runs on external services. Click "Run Online" to execute, or use the buttons to open in specialized PHP playgrounds.
    For Laravel projects, download and run locally with <code>php artisan serve</code>.
  </div>
  <div class="container">
    <div class="editor-container">
      <textarea class="code-editor" id="code" spellcheck="false">${phpCode.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
    </div>
    <div class="output-container">
      <div class="output-header">
        <span>Output</span>
        <span id="status"></span>
      </div>
      <div class="output-content" id="output">Click "Run Online" to execute your PHP code</div>
    </div>
  </div>

  <script>
    function getCode() {
      return document.getElementById('code').value;
    }

    function openIn3v4l() {
      // 3v4l.org accepts code via POST, so we create a form
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://3v4l.org/new';
      form.target = '_blank';

      const textarea = document.createElement('textarea');
      textarea.name = 'code';
      textarea.value = getCode();
      form.appendChild(textarea);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }

    function openInPhpFiddle() {
      window.open('https://phpfiddle.org/', '_blank');
    }

    async function runOnline() {
      const code = getCode();
      const output = document.getElementById('output');
      const status = document.getElementById('status');
      const runBtn = document.getElementById('runBtn');

      runBtn.disabled = true;
      runBtn.innerHTML = '<span class="spinner"></span> Running...';
      output.className = 'output-content';
      output.textContent = 'Executing PHP code...';
      status.textContent = '';

      try {
        // Use a CORS-friendly PHP execution service
        // Note: This uses a public PHP execution API
        const response = await fetch('https://api.paiza.io/runners/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source_code: code,
            language: 'php',
            input: '',
            longpoll: true,
            longpoll_timeout: 30
          })
        });

        const result = await response.json();

        if (result.stdout) {
          // Check if output looks like HTML
          if (result.stdout.trim().startsWith('<') || result.stdout.includes('<!DOCTYPE')) {
            output.className = 'output-content html-output';
            output.innerHTML = result.stdout;
          } else {
            output.className = 'output-content success';
            output.textContent = result.stdout;
          }
          status.textContent = 'Completed successfully';
        } else if (result.stderr) {
          output.className = 'output-content error';
          output.textContent = result.stderr;
          status.textContent = 'Error';
        } else if (result.build_stderr) {
          output.className = 'output-content error';
          output.textContent = result.build_stderr;
          status.textContent = 'Build error';
        } else {
          output.className = 'output-content';
          output.textContent = '(no output)';
          status.textContent = 'Completed';
        }
      } catch (err) {
        output.className = 'output-content error';
        output.textContent = 'Error: ' + err.message + '\\n\\nTry using 3v4l.org or PHP Fiddle for execution.';
        status.textContent = 'Failed';
      } finally {
        runBtn.disabled = false;
        runBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg> Run Online';
      }
    }

    // Support Ctrl+Enter to run
    document.getElementById('code').addEventListener('keydown', function(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        runOnline();
      }
      // Tab support
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = this.selectionStart;
        const end = this.selectionEnd;
        this.value = this.value.substring(0, start) + '    ' + this.value.substring(end);
        this.selectionStart = this.selectionEnd = start + 4;
      }
    });
  </script>
</body>
</html>`;
}

/**
 * Generate Godot Web Editor HTML
 * Opens the official Godot Web Editor in a new tab since it requires
 * SharedArrayBuffer with COOP/COEP headers that would break iframe embedding
 */
function generateGodotEditorHtml(gdscriptCode: string, title: string = 'Godot Project'): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #478cbf 0%, #2d5a7b 100%);
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }
    .container {
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      padding: 40px;
      max-width: 700px;
      text-align: center;
    }
    .godot-logo {
      width: 120px;
      height: 120px;
      margin-bottom: 24px;
    }
    h1 {
      color: #478cbf;
      font-size: 28px;
      margin-bottom: 16px;
    }
    p {
      color: #666;
      font-size: 16px;
      line-height: 1.6;
      margin-bottom: 24px;
    }
    .open-btn {
      background: #478cbf;
      color: white;
      border: none;
      padding: 16px 32px;
      font-size: 18px;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 10px;
      transition: background 0.2s, transform 0.2s;
    }
    .open-btn:hover {
      background: #3a7199;
      transform: translateY(-2px);
    }
    .limitations {
      margin-top: 32px;
      padding-top: 24px;
      border-top: 1px solid #eee;
      text-align: left;
    }
    .limitations h3 {
      color: #333;
      font-size: 14px;
      margin-bottom: 12px;
    }
    .limitations ul {
      color: #666;
      font-size: 13px;
      line-height: 1.8;
      padding-left: 20px;
    }
    .code-preview {
      margin-top: 24px;
      background: #1e1e1e;
      border-radius: 8px;
      padding: 16px;
      text-align: left;
      max-height: 200px;
      overflow-y: auto;
    }
    .code-preview pre {
      color: #d4d4d4;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 13px;
      white-space: pre-wrap;
      margin: 0;
    }
    .code-label {
      color: #888;
      font-size: 12px;
      margin-bottom: 8px;
    }
    .copy-btn {
      background: rgba(255,255,255,0.1);
      border: 1px solid rgba(255,255,255,0.2);
      color: #888;
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      cursor: pointer;
      float: right;
    }
    .copy-btn:hover {
      background: rgba(255,255,255,0.2);
      color: #fff;
    }
  </style>
</head>
<body>
  <div class="container">
    <svg class="godot-logo" viewBox="0 0 512 512" fill="#478cbf">
      <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm0 472c-119.3 0-216-96.7-216-216S136.7 40 256 40s216 96.7 216 216-96.7 216-216 216z"/>
      <path d="M256 88c-92.8 0-168 75.2-168 168s75.2 168 168 168 168-75.2 168-168S348.8 88 256 88zm-42 252c-22.1 0-40-17.9-40-40s17.9-40 40-40 40 17.9 40 40-17.9 40-40 40zm84 0c-22.1 0-40-17.9-40-40s17.9-40 40-40 40 17.9 40 40-17.9 40-40 40zm-42-112c-33.1 0-60-26.9-60-60h120c0 33.1-26.9 60-60 60z"/>
    </svg>
    <h1>Godot Web Editor</h1>
    <p>
      The Godot Engine runs in a separate browser tab due to technical requirements
      (SharedArrayBuffer). Click below to open the official Godot Web Editor.
    </p>
    <button class="open-btn" onclick="window.open('https://editor.godotengine.org/releases/latest/', '_blank')">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
      </svg>
      Open Godot Web Editor
    </button>

    ${
      gdscriptCode
        ? `
    <div class="code-preview">
      <div class="code-label">
        Your GDScript code (copy to Godot):
        <button class="copy-btn" onclick="copyCode()">Copy</button>
      </div>
      <pre id="code">${gdscriptCode.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
    </div>
    `
        : ''
    }

    <div class="limitations">
      <h3>Web Editor Limitations:</h3>
      <ul>
        <li>GDScript only (no C#/Mono support)</li>
        <li>No GDExtension plugins</li>
        <li>No debugging capabilities</li>
        <li>Cannot export projects</li>
        <li>Files saved to browser storage (IndexedDB)</li>
      </ul>
    </div>
  </div>

  <script>
    function copyCode() {
      const code = document.getElementById('code').textContent;
      navigator.clipboard.writeText(code).then(() => {
        const btn = document.querySelector('.copy-btn');
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
      });
    }
  </script>
</body>
</html>`;
}

/**
 * Generate a Mock Express simulation HTML page.
 * Creates an in-browser MockExpress that intercepts app.get/post/etc and provides
 * an endpoint tester UI, similar to the Flask mock for Python.
 */
function generateExpressSimulationHtml(serverCode: string, title: string = 'Express App'): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${title} - Express Mock Preview</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, monospace; background: #1a1a2e; color: #e0e0e0; min-height: 100vh; }
    .header { background: linear-gradient(135deg, #68a063, #3c6e35); padding: 12px 20px; display: flex; align-items: center; gap: 10px; }
    .header h1 { font-size: 16px; font-weight: 600; color: #fff; }
    .header .badge { background: rgba(255,255,255,0.15); padding: 2px 8px; border-radius: 10px; font-size: 11px; color: #e0e0e0; }
    .main { display: grid; grid-template-columns: 280px 1fr; height: calc(100vh - 44px); }
    .routes-panel { background: #16213e; border-right: 1px solid #2a2a4a; overflow-y: auto; padding: 12px; }
    .routes-panel h2 { font-size: 13px; color: #68a063; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em; }
    .route-item { padding: 6px 8px; margin: 2px 0; border-radius: 4px; cursor: pointer; font-size: 12px; display: flex; gap: 6px; align-items: center; transition: background 0.15s; }
    .route-item:hover { background: rgba(104,160,99,0.15); }
    .route-item.active { background: rgba(104,160,99,0.25); }
    .method-badge { font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 3px; text-transform: uppercase; min-width: 40px; text-align: center; }
    .method-GET { background: #2196f3; color: #fff; }
    .method-POST { background: #4caf50; color: #fff; }
    .method-PUT { background: #ff9800; color: #fff; }
    .method-DELETE { background: #f44336; color: #fff; }
    .method-PATCH { background: #9c27b0; color: #fff; }
    .tester { padding: 16px; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; }
    .tester h2 { font-size: 14px; color: #68a063; }
    .input-row { display: flex; gap: 8px; }
    .input-row select, .input-row input { background: #16213e; border: 1px solid #2a2a4a; color: #e0e0e0; padding: 8px 10px; border-radius: 4px; font-size: 13px; }
    .input-row select { width: 100px; }
    .input-row input { flex: 1; }
    .btn { background: #68a063; color: #fff; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 600; transition: background 0.15s; }
    .btn:hover { background: #5a8f56; }
    label { font-size: 12px; color: #8888aa; margin-bottom: 2px; display: block; }
    textarea { background: #16213e; border: 1px solid #2a2a4a; color: #e0e0e0; padding: 8px; border-radius: 4px; font-family: monospace; font-size: 12px; resize: vertical; min-height: 60px; width: 100%; }
    .response-panel { background: #0f0f23; border: 1px solid #2a2a4a; border-radius: 6px; padding: 12px; flex: 1; min-height: 150px; overflow: auto; }
    .response-status { font-size: 13px; margin-bottom: 8px; display: flex; gap: 8px; align-items: center; }
    .status-code { font-weight: 700; padding: 2px 8px; border-radius: 3px; }
    .status-2xx { background: #1b5e20; color: #a5d6a7; }
    .status-4xx { background: #b71c1c; color: #ef9a9a; }
    .status-5xx { background: #4a148c; color: #ce93d8; }
    .response-body { font-family: monospace; font-size: 12px; white-space: pre-wrap; word-break: break-all; color: #c0c0c0; }
    .no-routes { color: #666; font-size: 13px; padding: 16px; text-align: center; }
    .error-banner { background: #b71c1c; color: #ffcdd2; padding: 8px 12px; font-size: 12px; border-radius: 4px; margin: 8px 0; }
  </style>
</head>
<body>
  <div class="header">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
    <h1>${title}</h1>
    <span class="badge">Mock Server</span>
  </div>
  <div class="main">
    <div class="routes-panel">
      <h2>Routes</h2>
      <div id="routes-list"><div class="no-routes">Loading routes...</div></div>
    </div>
    <div class="tester">
      <h2>Endpoint Tester</h2>
      <div class="input-row">
        <select id="method">
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
          <option value="PATCH">PATCH</option>
        </select>
        <input type="text" id="url" placeholder="/" value="/" />
        <button class="btn" onclick="sendRequest()">Send</button>
      </div>
      <div>
        <label>Request Body (JSON)</label>
        <textarea id="body" placeholder='{"key": "value"}'></textarea>
      </div>
      <div>
        <label>Response</label>
        <div class="response-panel">
          <div class="response-status" id="resp-status"></div>
          <div class="response-body" id="resp-body">Click "Send" to test an endpoint</div>
        </div>
      </div>
      <div id="init-error"></div>
    </div>
  </div>
  <script>
    // ── MockExpress: simulates Express.js route handling in browser ──
    const registeredRoutes = [];
    const middlewares = [];

    function MockResponse() {
      this._status = 200;
      this._headers = { 'content-type': 'application/json' };
      this._body = null;
      this._sent = false;
      this.status = function(code) { this._status = code; return this; };
      this.json = function(data) { this._body = JSON.stringify(data, null, 2); this._headers['content-type'] = 'application/json'; this._sent = true; return this; };
      this.send = function(data) { this._body = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data); this._sent = true; return this; };
      this.sendStatus = function(code) { this._status = code; this._body = String(code); this._sent = true; return this; };
      this.set = function(k, v) { this._headers[k.toLowerCase()] = v; return this; };
      this.header = this.set;
      this.type = function(t) { this._headers['content-type'] = t; return this; };
      this.end = function(data) { if (data) this._body = String(data); this._sent = true; };
      this.redirect = function(url) { this._status = 302; this._headers['location'] = url; this._body = 'Redirecting to ' + url; this._sent = true; };
      this.render = function(view, data) { this._body = '<html><body><h1>' + view + '</h1><pre>' + JSON.stringify(data, null, 2) + '</pre></body></html>'; this._sent = true; };
    }

    function MockRequest(method, path, body) {
      this.method = method;
      this.path = path;
      this.url = path;
      this.originalUrl = path;
      this.baseUrl = '';
      this.params = {};
      this.query = {};
      this.body = body || {};
      this.headers = { 'content-type': 'application/json' };
      this.get = function(h) { return this.headers[h.toLowerCase()]; };
      // Parse query string
      const qIdx = path.indexOf('?');
      if (qIdx !== -1) {
        this.path = path.substring(0, qIdx);
        const qs = path.substring(qIdx + 1);
        qs.split('&').forEach(function(p) {
          const [k, v] = p.split('=');
          if (k) this.query[decodeURIComponent(k)] = decodeURIComponent(v || '');
        }.bind(this));
      }
    }

    function matchRoute(routePath, requestPath) {
      const routeParts = routePath.split('/').filter(Boolean);
      const reqParts = requestPath.split('/').filter(Boolean);
      if (routeParts.length !== reqParts.length) return null;
      const params = {};
      for (let i = 0; i < routeParts.length; i++) {
        if (routeParts[i].startsWith(':')) {
          params[routeParts[i].slice(1)] = decodeURIComponent(reqParts[i]);
        } else if (routeParts[i] !== reqParts[i]) {
          return null;
        }
      }
      return params;
    }

    function createApp() {
      const app = function() {};
      ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'].forEach(function(m) {
        app[m] = function(path) {
          const handlers = Array.prototype.slice.call(arguments, 1);
          registeredRoutes.push({ method: m.toUpperCase(), path: path, handlers: handlers });
        };
      });
      app.all = function(path) {
        const handlers = Array.prototype.slice.call(arguments, 1);
        ['GET','POST','PUT','DELETE','PATCH'].forEach(function(m) {
          registeredRoutes.push({ method: m, path: path, handlers: handlers });
        });
      };
      app.use = function() {
        const args = Array.prototype.slice.call(arguments);
        if (typeof args[0] === 'function') {
          middlewares.push({ path: '/', handler: args[0] });
        } else if (typeof args[0] === 'string' && typeof args[1] === 'function') {
          middlewares.push({ path: args[0], handler: args[1] });
        }
        // Ignore Router/static/etc — they can't run in browser
      };
      app.listen = function() { /* no-op in mock */ };
      app.set = function() { return app; };
      app.engine = function() { return app; };
      app.enable = function() { return app; };
      app.disable = function() { return app; };
      return app;
    }

    // Mock require/module system
    const mockModules = {
      express: function() { return createApp(); },
      cors: function() { return function(req, res, next) { next(); }; },
      path: { join: function() { return Array.prototype.slice.call(arguments).join('/'); }, resolve: function() { return Array.prototype.slice.call(arguments).join('/'); } },
      fs: { readFileSync: function() { return ''; }, existsSync: function() { return false; } },
      url: { parse: function(u) { return { pathname: u }; } },
      'body-parser': { json: function() { return function(req,res,next) { next(); }; }, urlencoded: function() { return function(req,res,next) { next(); }; } },
      dotenv: { config: function() {} },
      helmet: function() { return function(req, res, next) { next(); }; },
      'morgan': function() { return function(req, res, next) { next(); }; },
      'cookie-parser': function() { return function(req, res, next) { next(); }; },
    };
    mockModules.express.json = function() { return function(req, res, next) { next(); }; };
    mockModules.express.urlencoded = function() { return function(req, res, next) { next(); }; };
    mockModules.express.static = function() { return function(req, res, next) { next(); }; };
    mockModules.express.Router = function() { return createApp(); };

    function mockRequire(name) {
      if (mockModules[name]) return mockModules[name];
      return {};
    }

    // Initialize: evaluate user's server code
    let initError = null;
    try {
      const userCode = ${JSON.stringify(serverCode)};
      const wrappedCode = '(function(require, module, exports, __dirname, __filename, process) {\\n' + userCode + '\\n})';
      const fn = eval(wrappedCode);
      const mockModule = { exports: {} };
      const mockProcess = { env: { PORT: '3000', NODE_ENV: 'development' } };
      fn(mockRequire, mockModule, mockModule.exports, '/', '/server.js', mockProcess);
    } catch (err) {
      initError = err.message;
    }

    // Render route list
    function renderRoutes() {
      const list = document.getElementById('routes-list');
      if (registeredRoutes.length === 0) {
        list.innerHTML = '<div class="no-routes">' + (initError ? 'Failed to load routes' : 'No routes registered') + '</div>';
        return;
      }
      list.innerHTML = registeredRoutes.map(function(r, i) {
        return '<div class="route-item" onclick="selectRoute(' + i + ')">' +
          '<span class="method-badge method-' + r.method + '">' + r.method + '</span>' +
          '<span>' + r.path + '</span></div>';
      }).join('');
    }

    function selectRoute(idx) {
      const r = registeredRoutes[idx];
      document.getElementById('method').value = r.method;
      document.getElementById('url').value = r.path.replace(/:[^/]+/g, 'test');
      document.querySelectorAll('.route-item').forEach(function(el, i) {
        el.classList.toggle('active', i === idx);
      });
    }

    function sendRequest() {
      const method = document.getElementById('method').value;
      const url = document.getElementById('url').value || '/';
      let body = {};
      try { body = JSON.parse(document.getElementById('body').value || '{}'); } catch(e) { body = {}; }

      const req = new MockRequest(method, url, body);
      const res = new MockResponse();

      // Find matching route
      let matched = false;
      for (const route of registeredRoutes) {
        if (route.method !== method) continue;
        const params = matchRoute(route.path, req.path);
        if (params !== null) {
          req.params = params;
          matched = true;
          try {
            // Run middleware-style chain
            let handlerIdx = 0;
            function next(err) {
              if (err) { res.status(500).json({ error: String(err) }); return; }
              if (handlerIdx < route.handlers.length) {
                const h = route.handlers[handlerIdx++];
                try { h(req, res, next); } catch(e) { res.status(500).json({ error: e.message }); }
              }
            }
            next();
          } catch (err) {
            res.status(500).json({ error: err.message });
          }
          break;
        }
      }

      if (!matched) {
        res.status(404).json({ error: 'Cannot ' + method + ' ' + url });
      }

      // Display response
      const statusEl = document.getElementById('resp-status');
      const bodyEl = document.getElementById('resp-body');
      const statusClass = res._status < 400 ? 'status-2xx' : (res._status < 500 ? 'status-4xx' : 'status-5xx');
      statusEl.innerHTML = '<span class="status-code ' + statusClass + '">' + res._status + '</span><span>' + method + ' ' + url + '</span>';
      bodyEl.textContent = res._body || '(empty response)';
    }

    // Show init error if any
    if (initError) {
      document.getElementById('init-error').innerHTML = '<div class="error-banner">⚠ Server code error: ' + initError + '</div>';
    }
    renderRoutes();
  </script>
</body>
</html>`;
}

export const AlternativeRuntimeProvider = ({
  children,
  sessionId,
}: {
  children: ReactNode;
  sessionId?: string;
}) => {
  const [runtime, setRuntime] = useState<RuntimeType>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blobUrlRef = useRef<string | null>(null);

  // Cleanup blob URL on unmount
  const cleanupBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // Create a blob URL from HTML content (for preview URL support)
  const createBlobUrl = useCallback(
    (html: string): string => {
      cleanupBlobUrl();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      return url;
    },
    [cleanupBlobUrl],
  );

  // Detect framework from files
  const detectFramework = useCallback((files: Record<string, string>): RuntimeType => {
    const fileNames = Object.keys(files || {}).map((f) => f.toLowerCase());

    // Python detection
    if (
      fileNames.some((f) => f.endsWith('.py')) ||
      fileNames.includes('requirements.txt') ||
      fileNames.includes('pyproject.toml')
    ) {
      return 'pyodide';
    }

    // Flutter/Dart detection
    if (fileNames.some((f) => f.endsWith('.dart')) || fileNames.includes('pubspec.yaml')) {
      return 'dartpad';
    }

    // Rust detection
    if (fileNames.some((f) => f.endsWith('.rs')) || fileNames.includes('cargo.toml')) {
      return 'rust-playground';
    }

    // Go detection
    if (fileNames.some((f) => f.endsWith('.go')) || fileNames.includes('go.mod')) {
      return 'go-playground';
    }

    // PHP/Laravel detection
    if (
      fileNames.some((f) => f.endsWith('.php')) ||
      fileNames.includes('composer.json') ||
      fileNames.includes('artisan')
    ) {
      return 'php-playground';
    }

    // Godot detection
    if (
      fileNames.some((f) => f.endsWith('.gd')) ||
      fileNames.some((f) => f.endsWith('.tscn')) ||
      fileNames.some((f) => f.endsWith('.tres')) ||
      fileNames.includes('project.godot')
    ) {
      return 'godot-editor';
    }

    return null;
  }, []);

  // Generate Python preview
  const generatePythonPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        // Check if it looks like a Flask/FastAPI app
        const mainFile = files['main.py'] || files['app.py'] || files['server.py'];
        const isApiApp =
          mainFile &&
          (mainFile.includes('Flask') ||
            mainFile.includes('FastAPI') ||
            mainFile.includes('@app.route') ||
            mainFile.includes('@app.get'));

        let html: string;
        if (isApiApp) {
          html = generateFlaskSimulationHtml(files);
        } else {
          // Simple Python script
          const code =
            mainFile ||
            Object.values(files).find((c) => c.includes('print(')) ||
            'print("Hello, Python!")';
          html = generatePyScriptHtml(code, 'Python App');
        }

        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('pyodide');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate Python preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Generate Flutter preview
  const generateFlutterPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        // Find main.dart
        const mainDart =
          files['lib/main.dart'] ||
          files['main.dart'] ||
          Object.entries(files).find(([k]) => k.endsWith('.dart'))?.[1] ||
          '';

        const html = generateDartPadHtml(mainDart, 'Flutter App');
        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('dartpad');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate Flutter preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Generate Rust preview
  const generateRustPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        const mainRs =
          files['src/main.rs'] ||
          files['main.rs'] ||
          Object.entries(files).find(([k]) => k.endsWith('.rs'))?.[1] ||
          '';

        const html = generateRustPlaygroundHtml(mainRs, 'Rust App');
        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('rust-playground');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate Rust preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Generate Go preview
  const generateGoPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        const mainGo =
          files['main.go'] || Object.entries(files).find(([k]) => k.endsWith('.go'))?.[1] || '';

        const html = generateGoPlaygroundHtml(mainGo, 'Go App');
        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('go-playground');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate Go preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Generate PHP preview
  const generatePhpPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        // Find the main PHP file - prioritize common entry points
        const mainPhp =
          files['public/index.php'] ||
          files['index.php'] ||
          files['routes/web.php'] ||
          files['app.php'] ||
          Object.entries(files).find(([k]) => k.endsWith('.php'))?.[1] ||
          '<?php\necho "Hello, PHP!";';

        const html = generatePhpPlaygroundHtml(mainPhp, 'PHP App');
        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('php-playground');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate PHP preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Generate Godot preview (opens external editor)
  const generateGodotPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        // Find main GDScript file
        const mainGd =
          files['main.gd'] ||
          files['player.gd'] ||
          files['game.gd'] ||
          Object.entries(files).find(([k]) => k.endsWith('.gd'))?.[1] ||
          '';

        const html = generateGodotEditorHtml(mainGd, 'Godot Project');
        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('godot-editor');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate Godot preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Generate Express mock preview
  const generateExpressPreview = useCallback(
    async (files: Record<string, string>) => {
      setIsLoading(true);
      setError(null);

      try {
        // Find the main server file
        const mainServer =
          files['server.js'] ||
          files['app.js'] ||
          files['index.js'] ||
          files['src/server.js'] ||
          files['src/app.js'] ||
          files['src/index.js'] ||
          Object.entries(files).find(
            ([k, v]) =>
              k.endsWith('.js') &&
              (v.includes("require('express')") ||
                v.includes("from 'express'") ||
                v.includes('from "express"')),
          )?.[1] ||
          'const express = require("express");\nconst app = express();\napp.get("/", (req, res) => res.json({ message: "Hello Express!" }));\napp.listen(3000);';

        const html = generateExpressSimulationHtml(mainServer, 'Express App');
        setPreviewHtml(html);
        setPreviewUrl(createBlobUrl(html));
        setRuntime('express-playground');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate Express preview');
      } finally {
        setIsLoading(false);
      }
    },
    [createBlobUrl],
  );

  // Clear preview
  const clearPreview = useCallback(() => {
    cleanupBlobUrl();
    setPreviewUrl(null);
    setPreviewHtml(null);
    setRuntime(null);
    setError(null);
  }, [cleanupBlobUrl]);

  // Memoize context value to prevent infinite re-render cascades (React 19 error #185)
  const value = useMemo<AlternativeRuntimeContextType>(
    () => ({
      runtime,
      setRuntime,
      previewUrl,
      previewHtml,
      isLoading,
      error,
      generatePythonPreview,
      generateFlutterPreview,
      generateRustPreview,
      generateGoPreview,
      generatePhpPreview,
      generateGodotPreview,
      generateExpressPreview,
      detectFramework,
      clearPreview,
    }),
    [
      runtime,
      setRuntime,
      previewUrl,
      previewHtml,
      isLoading,
      error,
      generatePythonPreview,
      generateFlutterPreview,
      generateRustPreview,
      generateGoPreview,
      generatePhpPreview,
      generateGodotPreview,
      generateExpressPreview,
      detectFramework,
      clearPreview,
    ],
  );

  return (
    <AlternativeRuntimeContext.Provider value={value}>
      {children}
    </AlternativeRuntimeContext.Provider>
  );
};

export const useAlternativeRuntime = () => {
  const context = useContext(AlternativeRuntimeContext);
  if (context === undefined) {
    throw new Error('useAlternativeRuntime must be used within an AlternativeRuntimeProvider');
  }
  return context;
};

export const useOptionalAlternativeRuntime = () => {
  return useContext(AlternativeRuntimeContext) ?? null;
};
